import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, RefreshCcw, CheckCircle2, CalendarClock, UserPlus, Trophy,
  HandCoins, Target, AlertTriangle, ArrowLeft, IndianRupee, Activity,
} from 'lucide-react';
import { AgentLayout } from '../../components/AgentLayout';
import api from '../../api';

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function presetRange(key) {
  const now = new Date();
  if (key === 'today') return { from: ymd(now), to: ymd(now) };
  if (key === 'yesterday') {
    const y = new Date(now); y.setDate(now.getDate() - 1);
    return { from: ymd(y), to: ymd(y) };
  }
  if (key === 'week') {
    const day = now.getDay() || 7;
    const monday = new Date(now); monday.setDate(now.getDate() - (day - 1));
    return { from: ymd(monday), to: ymd(now) };
  }
  if (key === 'month') {
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: ymd(first), to: ymd(now) };
  }
  if (key === 'last30') {
    const from = new Date(now); from.setDate(now.getDate() - 29);
    return { from: ymd(from), to: ymd(now) };
  }
  return { from: ymd(now), to: ymd(now) };
}

const PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yest.' },
  { key: 'week', label: 'This Wk' },
  { key: 'month', label: 'This Mo' },
  { key: 'last30', label: 'Last 30d' },
];

function fmtINR(n) {
  const num = Number(n || 0);
  if (num >= 10000000) return `\u20B9${(num / 10000000).toFixed(2)} Cr`;
  if (num >= 100000) return `\u20B9${(num / 100000).toFixed(2)} L`;
  if (num >= 1000) return `\u20B9${(num / 1000).toFixed(1)} K`;
  return `\u20B9${num.toFixed(0)}`;
}

function fmtDay(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }); }
  catch { return d; }
}
function fmtTime(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }); }
  catch { return ''; }
}

const TYPE_META = {
  visit:      { label: 'Visit',      icon: CheckCircle2, color: '#16a34a', bg: '#dcfce7' },
  reschedule: { label: 'Reschedule', icon: CalendarClock, color: '#b45309', bg: '#fef3c7' },
  register:   { label: 'New Lead',   icon: UserPlus,      color: '#1d4ed8', bg: '#dbeafe' },
  onboard:    { label: 'Onboard',    icon: Trophy,        color: '#7c3aed', bg: '#ede9fe' },
  payment:    { label: 'Payment',    icon: IndianRupee,   color: '#0891b2', bg: '#cffafe' },
};

const PAYMENT_MODE_LABELS = {
  cash: 'Cash', upi: 'UPI', neft: 'NEFT', rtgs: 'RTGS',
  imps: 'IMPS', cheque: 'Cheque', card: 'Card', other: 'Other',
};

export default function MyPerformance() {
  const navigate = useNavigate();
  const user = (() => { try { return JSON.parse(localStorage.getItem('au_user') || '{}'); } catch { return {}; } })();

  const [preset, setPreset] = useState('week');
  const [range, setRange] = useState(presetRange('week'));
  const [activity, setActivity] = useState(null);
  const [perf, setPerf] = useState(null);          // recovery-performance row (self)
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const pickPreset = (key) => {
    setPreset(key);
    setRange(presetRange(key));
  };

  // Map UI preset to recovery-performance period (week | month | all). last30 → month.
  const perfPeriod = useMemo(() => {
    if (preset === 'today' || preset === 'yesterday' || preset === 'week') return 'week';
    if (preset === 'last30') return 'month';
    return preset === 'month' ? 'month' : 'all';
  }, [preset]);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const params = { from: range.from, to: range.to, limit: 500, me: 1 };
      const [actRes, perfRes, payRes] = await Promise.all([
        api.get('/reports/field-activity', { params }),
        api.get('/reports/recovery-performance', { params: { period: perfPeriod, me: 1 } }),
        api.get('/recovery/payments', { params }),
      ]);
      setActivity(actRes.data || null);
      const list = Array.isArray(perfRes.data) ? perfRes.data : [];
      // Self is first (and usually only) entry, but defend against returning all in case of role mismatch
      setPerf(list.find((r) => r.id === user.id) || list[0] || null);
      // /recovery/payments returns { summary, items: [...] }
      const payData = payRes.data;
      const payItems = Array.isArray(payData) ? payData : (payData?.items || []);
      setPayments(payItems);
    } catch (e) {
      console.error(e);
      setErr(e.response?.data?.detail || 'Failed to load performance data');
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, perfPeriod, user.id]);

  useEffect(() => { load(); }, [load]);

  const sum = activity?.summary;
  const myAgentRow = useMemo(() => {
    if (!sum?.byAgent) return null;
    return sum.byAgent.find((a) => a.agentId === user.id) || sum.byAgent[0] || null;
  }, [sum, user.id]);

  const visits = myAgentRow?.visits ?? sum?.byType?.visit ?? 0;
  const reschedules = myAgentRow?.reschedules ?? sum?.byType?.reschedule ?? 0;
  const registers = myAgentRow?.registers ?? sum?.byType?.register ?? 0;
  const onboards = myAgentRow?.onboards ?? sum?.byType?.onboard ?? 0;
  const uniqueMerch = myAgentRow?.uniqueMerchants ?? sum?.uniqueMerchants ?? 0;

  // Combined activity feed (merchant + payments) — newest first, capped
  const feed = useMemo(() => {
    const items = [];
    (activity?.items || []).forEach((i) => {
      items.push({
        kind: i.type, when: i.when,
        title: i.merchantName || i.ownerName || 'Merchant',
        sub: i.note || '',
        chip: i.merchantStatus || '',
      });
    });
    (payments || []).forEach((p) => {
      items.push({
        kind: 'payment',
        when: p.collectedAt || p.createdAt,
        title: p.customerName || p.clientId || p.loanAccountNumber,
        sub: `${fmtINR(p.amount)} via ${PAYMENT_MODE_LABELS[p.paymentMode] || p.paymentMode || 'Mode'}`,
        chip: p.loanAccountNumber,
      });
    });
    items.sort((a, b) => new Date(b.when).getTime() - new Date(a.when).getTime());
    return items.slice(0, 40);
  }, [activity, payments]);

  // Per-day bar chart data (max value used for scaling)
  const byDay = sum?.byDay || [];
  const maxDay = byDay.reduce((m, d) => Math.max(m, d.total || 0), 0);

  const totalPaymentsAmount = payments.reduce((acc, p) => acc + Number(p.amount || 0), 0);

  return (
    <AgentLayout>
      <div className="au-page">
        {/* Header */}
        <div className="au-greeting fu" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              type="button"
              onClick={() => navigate(-1)}
              aria-label="Back"
              style={{ background: 'var(--g1)', border: 'none', borderRadius: 8, padding: 6, cursor: 'pointer', color: 'var(--navy)' }}
            >
              <ArrowLeft size={16} />
            </button>
            <div>
              <div className="greet-hi">My Performance</div>
              <div className="greet-dt">{user.name?.split(' ')[0] || 'You'} · {range.from} → {range.to}</div>
            </div>
          </div>
          <button className="ov-gps-btn" onClick={load} style={{ background: 'var(--g1)', color: 'var(--navy)' }} aria-label="Refresh">
            <RefreshCcw size={14} />
          </button>
        </div>

        {/* Date range chips */}
        <div className="card fu" style={{ padding: 10, marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {PRESETS.map((p) => {
              const active = preset === p.key;
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => pickPreset(p.key)}
                  style={{
                    border: '1px solid ' + (active ? 'var(--navy)' : 'var(--g2)'),
                    background: active ? 'var(--navy)' : '#fff',
                    color: active ? '#fff' : 'var(--navy)',
                    padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 10, alignItems: 'center' }}>
            <input
              type="date"
              className="form-input"
              style={{ flex: 1, padding: '6px 8px', fontSize: 12 }}
              value={range.from}
              onChange={(e) => { setPreset('custom'); setRange((r) => ({ ...r, from: e.target.value })); }}
            />
            <span style={{ color: 'var(--g3)', fontSize: 11 }}>to</span>
            <input
              type="date"
              className="form-input"
              style={{ flex: 1, padding: '6px 8px', fontSize: 12 }}
              value={range.to}
              onChange={(e) => { setPreset('custom'); setRange((r) => ({ ...r, to: e.target.value })); }}
            />
          </div>
        </div>

        {err && (
          <div className="ov-ban fu" style={{ background: 'var(--red)' }}>
            <div className="ov-ico"><AlertTriangle size={18} color="#fff" /></div>
            <div className="ov-body"><div className="ov-title">{err}</div></div>
          </div>
        )}

        {/* Field activity KPI grid */}
        <div className="sec-hd fu">
          <span className="sec-t"><Activity size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Field Activity</span>
          <span style={{ fontSize: 11, color: 'var(--g3)' }}>{uniqueMerch} unique merchant{uniqueMerch === 1 ? '' : 's'}</span>
        </div>
        <div className="au-stats" data-testid="my-activity-stats">
          <KpiTile cls="fu-1" label="Visits" value={visits} accent="#16a34a" />
          <KpiTile cls="fu-2" label="New Leads" value={registers} accent="#1d4ed8" />
          <KpiTile cls="fu-3" label="Onboarded" value={onboards} accent="#7c3aed" />
          <KpiTile cls="fu-4" label="Reschedules" value={reschedules} accent="#b45309" />
        </div>

        {/* Per-day mini chart */}
        {byDay.length > 0 && (
          <div className="card fu" style={{ padding: 12, marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)', marginBottom: 8 }}>
              Daily activity
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 80, overflowX: 'auto' }}>
              {byDay.map((d) => {
                const h = maxDay > 0 ? Math.max(4, (d.total / maxDay) * 70) : 4;
                return (
                  <div key={d.date} title={`${d.date}: ${d.total}`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 24 }}>
                    <div style={{ width: 18, height: h, background: 'var(--navy)', borderRadius: 4 }} />
                    <div style={{ fontSize: 9, color: 'var(--g3)', marginTop: 4, transform: 'rotate(-30deg)', transformOrigin: 'center', whiteSpace: 'nowrap' }}>
                      {d.date.slice(5)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Collection KPI grid */}
        <div className="sec-hd fu">
          <span className="sec-t"><HandCoins size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Collections ({perfPeriod === 'week' ? 'last 7d' : perfPeriod === 'month' ? 'last 30d' : 'all-time'})</span>
        </div>
        <div className="au-stats">
          <KpiTile
            cls="fu-1"
            label="Collected"
            value={fmtINR(perf?.paymentsAmount ?? totalPaymentsAmount)}
            sub={`${perf?.paymentsCount ?? payments.length} txn`}
            accent="#0891b2"
          />
          <KpiTile
            cls="fu-2"
            label="Active Cases"
            value={perf?.casesAssigned ?? 0}
            sub={fmtINR(perf?.totalOverdue)}
            accent="#0b2545"
          />
          <KpiTile
            cls="fu-3"
            label="PTPs Kept"
            value={`${perf?.ptpKeepRate ?? 0}%`}
            sub={`${(perf?.ptpsCreated ?? 0) - (perf?.ptpsMissed ?? 0)}/${perf?.ptpsCreated ?? 0}`}
            accent="#16a34a"
          />
          <KpiTile
            cls="fu-4"
            label="Recovery Rate"
            value={`${perf?.recoveryRate ?? 0}%`}
            sub={`${perf?.actionsLogged ?? 0} actions`}
            accent="#7c3aed"
          />
        </div>

        {(perf?.ptpsMissed || 0) > 0 && (
          <div className="ov-ban fu" style={{ background: 'var(--red)' }}>
            <div className="ov-ico"><AlertTriangle size={18} color="#fff" /></div>
            <div className="ov-body">
              <div className="ov-title">
                {perf.ptpsMissed} missed promise{perf.ptpsMissed !== 1 ? 's' : ''} in this period
              </div>
              <div style={{ fontSize: 12, color: '#fee2e2' }}>
                Reach out to those customers — see Recovery → Cases.
              </div>
            </div>
          </div>
        )}

        {/* Recent collections */}
        <div className="sec-hd fu">
          <span className="sec-t"><Target size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Recent Collections</span>
          <span style={{ fontSize: 11, color: 'var(--g3)' }}>{payments.length} in range</span>
        </div>
        {loading ? (
          <div className="empty-state fu">Loading…</div>
        ) : payments.length === 0 ? (
          <div className="empty-state fu">
            <div style={{ fontSize: '32px' }}>💸</div>
            <div className="empty-title">No collections yet</div>
            <div className="empty-text">Payments you record will show here.</div>
          </div>
        ) : (
          payments.slice(0, 10).map((p, i) => (
            <div
              key={p.id || `${p.loanAccountNumber}-${p.collectedAt}-${i}`}
              className={`mcard fu-${Math.min(5, i + 1)}`}
              onClick={() => navigate(`/recovery/cases/${encodeURIComponent(p.loanAccountNumber)}`)}
              style={{ cursor: 'pointer' }}
            >
              <div className="mc-body">
                <div className="mc-top">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="mc-nm">{p.customerName || p.clientId || p.loanAccountNumber}</div>
                    <div className="mc-ow">{p.loanAccountNumber} · {fmtDay(p.collectedAt)} {fmtTime(p.collectedAt)}</div>
                  </div>
                  <span className="status-pill badge-today" style={{ fontSize: '10px', background: '#cffafe', color: '#0e7490' }}>
                    {PAYMENT_MODE_LABELS[p.paymentMode] || p.paymentMode}
                  </span>
                </div>
                <div className="mc-foot">
                  <div>
                    <div style={{ fontSize: '10px', color: 'var(--g3)' }}>AMOUNT</div>
                    <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: '15px' }}>{fmtINR(p.amount)}</div>
                  </div>
                  {p.note && (
                    <div style={{ fontSize: 11, color: 'var(--g3)', textAlign: 'right', maxWidth: '50%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.note}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}

        {/* Activity feed */}
        <div className="sec-hd fu">
          <span className="sec-t"><TrendingUp size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Activity Feed</span>
          <span style={{ fontSize: 11, color: 'var(--g3)' }}>{feed.length} item{feed.length === 1 ? '' : 's'}</span>
        </div>
        {feed.length === 0 ? (
          <div className="empty-state fu">
            <div style={{ fontSize: '32px' }}>📋</div>
            <div className="empty-title">No activity in this range</div>
            <div className="empty-text">Log visits and record collections to see them here.</div>
          </div>
        ) : (
          <div className="card fu" style={{ padding: 0 }}>
            {feed.map((it, i) => {
              const meta = TYPE_META[it.kind] || TYPE_META.visit;
              const Icon = meta.icon;
              return (
                <div
                  key={`${it.when}-${i}`}
                  style={{
                    display: 'flex', gap: 10, padding: '10px 12px',
                    borderTop: i === 0 ? 'none' : '1px solid var(--g1)',
                    alignItems: 'flex-start',
                  }}
                >
                  <div style={{
                    width: 32, height: 32, minWidth: 32, borderRadius: 8,
                    background: meta.bg, color: meta.color,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Icon size={16} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {meta.label} · {it.title}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--g3)', whiteSpace: 'nowrap' }}>
                        {fmtDay(it.when)} {fmtTime(it.when)}
                      </div>
                    </div>
                    {it.sub && (
                      <div style={{ fontSize: 12, color: 'var(--g3)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {it.sub}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ height: 24 }} />
      </div>
    </AgentLayout>
  );
}

function KpiTile({ cls, label, value, sub, accent }) {
  return (
    <div className={`au-stile ${cls || ''}`} style={accent ? { borderTop: `3px solid ${accent}` } : undefined}>
      <div className="slbl">{label}</div>
      <div className="sval">{value}</div>
      {sub && <div className="ssub">{sub}</div>}
    </div>
  );
}
